import React from "react";
import { Link } from "react-router-dom";
import { Compass, Telescope, Users, Award, ChevronRight } from "lucide-react";

const STORM_IMG = "https://images.unsplash.com/photo-1508697014387-db70aad34f4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxsaWdodG5pbmclMjBzdG9ybSUyMG5pZ2h0JTIwY2luZW1hdGljfGVufDB8fHx8MTc4MDcxNzgxMnww&ixlib=rb-4.1.0&q=85";
const TEXTURE = "https://images.pexels.com/photos/12372778/pexels-photo-12372778.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function About() {
  return (
    <div data-testid="about-page" className="grain">
      <section className="relative h-[60vh] min-h-[460px] flex items-end overflow-hidden">
        <img src={STORM_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        <div className="container-vg relative pb-16">
          <div className="overline">About Vortex Ghost</div>
          <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter mt-4 max-w-4xl">
            Built by chasers. <br /><span className="text-yellow-400">Driven by data.</span>
          </h1>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-vg grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">Origin</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mt-3">From backyard observers to field scientists.</h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-zinc-300 leading-relaxed text-lg">
            <p>
              Vortex Ghost was founded in 2019 by a small group of meteorology students, atmospheric scientists, and
              veteran storm chasers who shared a single conviction — that the gap between research-grade observations
              and real-time field reality was too wide.
            </p>
            <p>
              The team began with two vehicles, a handheld anemometer, and a relentless commitment to safety-first
              chasing. Today we operate three custom-built mesonet vehicles, a balloon-launching support truck, and a
              network of fixed observation sites across the central United States.
            </p>
            <p>
              Our work has been cited by university research groups, NWS local offices, and contributed to two refereed
              papers on tornado boundary-layer dynamics. We remain proudly independent and entirely non-profit.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-pad bg-[#0d0f12] border-y border-white/5">
        <div className="container-vg">
          <div className="overline">Operating Principles</div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold mt-3 max-w-3xl">Four lines we refuse to cross.</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {[
              { icon: Compass, t: "Safety First", d: "No data point is worth a life. Bail-out routes are planned before deployment, always." },
              { icon: Telescope, t: "Science Always", d: "Every chase contributes calibrated, time-stamped, peer-reviewable observations." },
              { icon: Users, t: "Crew Discipline", d: "Hierarchical comms, clear roles, and zero ego on the radio. The storm is the boss." },
              { icon: Award, t: "Public Service", d: "Real-time ground-truth reports flow to NWS and local emergency management." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="card-vg p-7" data-testid={`value-${t.toLowerCase().replace(/\s+/g, '-')}`}>
                <Icon className="w-7 h-7 text-yellow-400" />
                <h3 className="font-heading text-xl font-semibold mt-4">{t}</h3>
                <p className="text-zinc-400 mt-3 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-pad">
        <div className="container-vg">
          <div className="overline">Timeline</div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold mt-3">Seven seasons of chasing.</h2>

          <div className="mt-12 space-y-6">
            {[
              { y: "2019", t: "Team founded with two chase vehicles" },
              { y: "2020", t: "First peer-reviewed publication contributed" },
              { y: "2021", t: "Mobile mesonet vehicle #2 commissioned" },
              { y: "2022", t: "Partnership with University of Oklahoma SoM" },
              { y: "2023", t: "Launched balloon sounding support unit" },
              { y: "2024", t: "Documented EF-4 in Greenfield, IA" },
              { y: "2025", t: "Expanded operations into Dixie Alley" },
              { y: "2026", t: "Recruiting new field operators — that's you?" },
            ].map(({ y, t }, i) => (
              <div key={y} className="flex items-start gap-6 border-l border-white/10 pl-6 hover:border-yellow-400 transition-colors">
                <div className="font-mono text-yellow-400 text-lg w-16 shrink-0">{y}</div>
                <div className="text-zinc-200 text-lg">{t}</div>
              </div>
            ))}
          </div>

          <div className="mt-16 flex flex-wrap gap-4">
            <Link to="/apply" className="btn-primary">Join Season 8 <ChevronRight className="w-4 h-4" /></Link>
            <Link to="/research" className="btn-secondary">View research</Link>
          </div>
        </div>
      </section>

      <section className="relative h-[40vh] overflow-hidden">
        <img src={TEXTURE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
        <div className="container-vg relative h-full flex items-center">
          <blockquote className="font-heading text-3xl md:text-4xl font-medium max-w-3xl leading-tight">
            "The storm doesn't care about your career. It cares about your respect."
            <footer className="overline mt-4 !text-zinc-500">— Vortex Ghost field handbook, p. 3</footer>
          </blockquote>
        </div>
      </section>
    </div>
  );
}
